/* =========================================================================
   T7-V — the Stoll 13 s film. One script, three placements (PDP gallery slide,
   doors collection band, homepage efx-proof column). Presentational only:
   it never touches the order path, the configurator, or the cart.

   Contract with the markup (snippets/efx-video-figure.liquid):
     [data-efv]           root                (.efv)
     [data-efv-video]     the <video>         preload="none", no autoplay/loop/muted
     [data-efv-play]      the play control    48/56 px, ink stroke
     data-efv-label       the dataLayer label, one push per label per page view
     data-efv-480 / -720  the two theme-asset URLs, so the phone never pulls 720p

   Rules obeyed:
     * click to play. Nothing is fetched before the click (preload="none").
     * no autoplay, no loop, no muted-autoplay fallback.
     * on `ended` the poster and the control come back, and the control says Replay.
     * prefers-reduced-motion is a no-op here: a click is a click either way, and
       the only motion this file could cause (scrollIntoView) is not performed.
   ========================================================================= */
(function () {
  'use strict';

  var pushed = {};

  function pushPlay(label) {
    if (!label || pushed[label]) return;
    pushed[label] = true;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'video_play', label: label });
    } catch (e) {}
  }

  /* The <source media> pair is what ships in the markup so the component works
     with JS off. Browsers differ on whether they honour `media` on a <source>
     inside <video>, so on the first click — before any byte has been requested,
     because preload is "none" — the source that does not apply is removed and
     the element is loaded once. Deterministic, and still one file per play. */
  function resolveSource(root, video) {
    if (video.__efvResolved) return;
    video.__efvResolved = true;
    var small = false;
    try { small = window.matchMedia('(max-width: 767px)').matches; } catch (e) {}
    var want = small ? root.getAttribute('data-efv-480') : root.getAttribute('data-efv-720');
    if (!want) return;
    var sources = video.querySelectorAll('source');
    var kept = false;
    Array.prototype.forEach.call(sources, function (s) {
      if (s.getAttribute('src') === want && !kept) { kept = true; s.removeAttribute('media'); }
      else { s.parentNode.removeChild(s); }
    });
    if (!kept) video.setAttribute('src', want);
    try { video.load(); } catch (e) {}
  }

  function wireEmbed(root) {
    if (root.__efvWired) return;
    var button = root.querySelector('[data-efv-play]'), close = root.querySelector('[data-efv-close]');
    var host = root.getAttribute('data-efv-embed'), id = root.getAttribute('data-efv-id');
    if (!button || !close || (host === 'youtube' ? !/^[A-Za-z0-9_-]{11}$/.test(id) : host !== 'vimeo' || !/^\d+$/.test(id))) return;
    root.__efvWired = true;
    button.addEventListener('click', function () {
      if (root.querySelector('iframe')) return;
      var frame = document.createElement('iframe');
      frame.className = 'efv__video'; frame.title = root.getAttribute('data-efv-title') || 'Gas log video';
      frame.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture'; frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      frame.src = host === 'youtube' ? 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0&playsinline=1' : 'https://player.vimeo.com/video/' + id + '?autoplay=1&dnt=1&title=0&byline=0';
      root.querySelector('.efv__stage').appendChild(frame); root.classList.add('efv--playing'); close.hidden = false;
      frame.focus(); pushPlay(root.getAttribute('data-efv-label'));
    });
    function stop() { var frame = root.querySelector('iframe'); if (frame) frame.remove(); root.classList.remove('efv--playing'); close.hidden = true; button.focus(); }
    close.addEventListener('click', stop);
    root.addEventListener('keydown', function (event) { if (event.key === 'Escape' && root.classList.contains('efv--playing')) stop(); });
  }

  function wire(root) {
    if (root.hasAttribute('data-efv-embed')) { wireEmbed(root); return; }
    if (root.__efvWired) return;
    root.__efvWired = true;

    var video = root.querySelector('[data-efv-video]');
    var btn = root.querySelector('[data-efv-play]');
    if (!video || !btn) return;

    var label = root.getAttribute('data-efv-label') || '';
    var playLabel = btn.getAttribute('aria-label') || 'Play the film';
    var replayLabel = btn.getAttribute('data-efv-replay-label') || 'Replay the film';

    function toPlaying() {
      root.classList.remove('efv--ended');
      root.classList.add('efv--playing');
    }
    function toIdle(ended) {
      root.classList.remove('efv--playing');
      if (ended) {
        root.classList.add('efv--ended');
        btn.setAttribute('aria-label', replayLabel);
      } else {
        btn.setAttribute('aria-label', playLabel);
      }
    }

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      resolveSource(root, video);
      toPlaying();
      var p;
      try { p = video.play(); } catch (err) { p = null; }
      if (p && p.catch) p.catch(function () { toIdle(false); });
      pushPlay(label);
    });

    /* the native control bar can also start it once the element is visible */
    video.addEventListener('play', function () { toPlaying(); pushPlay(label); });
    video.addEventListener('ended', function () {
      try { video.pause(); video.currentTime = 0; } catch (e) {}
      toIdle(true);
    });
  }

  /* PDP only: the note under the thumbnail rail selects the film slide. The
     gallery is Swiper (assets/ef-gallery-swiper.js exposes window.EFGallery);
     if that is not there yet, clicking the thumbnail is the fallback, which is
     what a real tap on the rail does anyway. */
  function wireGoto(btn) {
    if (btn.__efvWired) return;
    btn.__efvWired = true;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var main = document.getElementById('efGallerySwiper');
      if (!main) return;
      var slides = main.querySelectorAll('.swiper-slide');
      var idx = -1;
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].getAttribute('data-media-type') === 'efvideo') idx = i;
      }
      if (idx < 0) return;
      var done = false;
      try {
        if (window.EFGallery && window.EFGallery.main) { window.EFGallery.main.slideTo(idx); done = true; }
      } catch (err) {}
      if (!done) {
        var t = document.querySelector('#efGalleryThumbs .ef-pd-thumb--efv');
        if (t) t.click();
      }
      try { main.scrollIntoView({ block: 'nearest' }); } catch (err) {}
    });
  }

  function scan(scope) {
    var root = scope || document;
    Array.prototype.forEach.call(root.querySelectorAll('[data-efv]'), wire);
    Array.prototype.forEach.call(root.querySelectorAll('[data-efv-goto]'), wireGoto);
  }

  function start() {
    scan(document);
    /* the PDP gallery slide is moved around by Swiper and the collection grid
       paints more cards on "Load more" — re-scan cheaply when the DOM changes. */
    try {
      var mo = new MutationObserver(function () { scan(document); });
      mo.observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.EFVideo = { scan: scan };
})();
